import React from 'react';
import {
  formatAsMoneyString,
  formatAsPercentString,
  sortByNumberOrString
} from '../../../../utils/utils';
import {
  calculateDiscount,
  getInventoryMaintenance
} from '../../../../utils/renewals-util';
import { cloneDeep } from 'lodash';
import { isFieldVisible } from '../../../../utils/bill-method-util';
import {getRecentProductChangeType} from './product-list-page-util';

const getMerchandiseOrder = (typeOfMerchandise) =>
  typeOfMerchandise.toUpperCase() === 'GARMENTS' ? 0 : 1;

const getAdjustedCellComponent = (base, adjusted, requiresApproval, discount) =>
  adjusted != null && base !== adjusted ? (
    <div>
      <div style={{ textDecoration: 'line-through' }}>{base}</div>
      <div
        className={`${requiresApproval ? 'adjustment-text' : ''}`}
      >{`${adjusted} ${
        !!discount
          ? `(${discount > 0 ? '-' : ''}${formatAsPercentString(
            Math.abs(discount),
            false
          )})`
          : ''
      }`}</div>
    </div>
  ) : (
    base
  );

const isWithinTriggerPeriod = (lineItem, triggerDays=60) => {
  const now = new Date();
  const checkDate = (date) => {
    if (!date) return false;
    const parsed = new Date(date);
    const diffDays = Math.abs(now - parsed) / (1000 * 60 * 60 * 24);
    return diffDays <= triggerDays;
  };
  return checkDate(lineItem.plpcDateActive) || checkDate(lineItem.plpcDateInactive);
}

const sortDirection = {
  asc: {
    sortBy: (a, b) => sortByNumberOrString(a, b)
  },
  desc: {
    sortBy: (a, b) => sortByNumberOrString(b, a)
  }
};

const sortBys = {
  description: (direction) => (a, b) =>
    sortDirection[direction].sortBy(a.description.name, b.description.name),
  rentalPrice: (direction) => (a, b) =>
    sortDirection[direction].sortBy(a.rentalPrice.base, b.rentalPrice.base),
  salesPrice: (direction) => (a, b) =>
    sortDirection[direction].sortBy(a.salesPrice.base, b.salesPrice.base),
  washPrice: (direction) => (a, b) =>
    sortDirection[direction].sortBy(a.washPrice.base, b.washPrice.base),
  replacePrice: (direction) => (a, b) =>
    sortDirection[direction].sortBy(a.replacePrice.base, b.replacePrice.base),
  quantity: (direction) => (a, b) =>
    sortDirection[direction].sortBy(a.quantity.base, b.quantity.base),
  circulatingInventory: (direction) => (a, b) =>
    sortDirection[direction].sortBy(
      a.circulatingInventory.base,
      b.circulatingInventory.base
    ),
  weeklyUsage: (direction) => (a, b) =>
    sortDirection[direction].sortBy(
      a.weeklyUsage.base,
      b.weeklyUsage.base
    ),
  inventoryMaintenance: (direction) => (a, b) =>
    sortDirection[direction].sortBy(
      a.inventoryMaintenance,
      b.inventoryMaintenance
    ),
  easyCarePrice: (direction) => (a, b) =>
    sortDirection[direction].sortBy(a.easyCarePrice, b.easyCarePrice),
  DEFAULT: (direction) => (a, b) => defaultComparer(a, b)
};

const defaultComparer = (a, b) => {
  const garmentsComparison =
    getMerchandiseOrder(a.typeOfMerchandise) -
    getMerchandiseOrder(b.typeOfMerchandise);
  if (garmentsComparison === 0) {
    return a.productCode.localeCompare(b.productCode);
  }
  return garmentsComparison;
};

const priceColumns = ['rentalPrice', 'salesPrice', 'washPrice', 'replacePrice'];

const getDynamicHeaderLabel = (lineItems, appConfigs) => {
  if (!Array.isArray(lineItems)) return '';

  let hasAdded = false;
  let hasRemoved = false;

  const triggerDays = parseInt(
    appConfigs?.TRIGGER_PERIOD?.value || '60', 10
  );

  for (const item of lineItems) {
    const { added, removed } = getRecentProductChangeType(item, triggerDays);
    hasAdded = hasAdded || added;
    hasRemoved = hasRemoved || removed;
    if (hasAdded && hasRemoved) break;
  }
  const addedAndRemovedText = 'Product Added (+)\nProduct Removed (-)';
  if (hasAdded && hasRemoved) return  addedAndRemovedText.split("\n").map((line, index) => (
      <React.Fragment key={index}>
        {line}
        <br />
      </React.Fragment>
    ));
  if (hasAdded) return 'Product Added (+)';
  if (hasRemoved) return 'Product Removed (–)';
  return '';
};

const columns = (props) => {
  const triggerDays = parseInt(
    props.appConfigs?.TRIGGER_PERIOD?.value || '60', 10
  );

  return [
  {
    header: {
      display: props.intl.formatMessage({ id: 'product' }),
      sortBy: 'description'
    },
    accessor: (lineItem) => ({
      display: (
        <div>
          <div>{lineItem.description.name}</div>
          <b>{lineItem.description.code}</b>
          <div>{lineItem.size}</div>
        </div>
      )
    })
  },
  {
    header: {
      display: getDynamicHeaderLabel(props.lineItems, props.appConfigs)
    },
    accessor: (lineItem) => {
      const {added, removed} = getRecentProductChangeType(lineItem, triggerDays);
      let symbol = '';
      let secondarySymbol = '';
      let className = '';
      let secondaryClassName = '';
      if(added && removed){
        symbol = '+';
        className = 'trigger-icon plus';
        secondarySymbol = '-';
        secondaryClassName = 'trigger-icon minus';
        return {
          display: (
            <>
              {added && <div className={className}> {symbol} </div>}
              {removed && <div className={secondaryClassName}> {secondarySymbol} </div>}
            </>
          )
        };
      }
      else if (added) {
        symbol = '+';
        className = 'trigger-icon plus';
        return {
          display: (
            <>
              {added && <div className={className}> {symbol} </div>}
            </>
          )
        };
      } else if (removed) {
        symbol = '–';
        className = 'trigger-icon minus';
        return {
          display: (
            <>
              {removed && <div className={className}> {symbol} </div>}
            </>
          )
        };
      }
    }
  },
  {
    header: {
      display: props.intl.formatMessage({ id: 'number_of_wearers' })
    },
    accessor: (lineItem) => ({
      display: isFieldVisible(
        'numberOfWearers',
        lineItem.billMethod,
        lineItem.numberOfWearers,
        lineItem.typeOfMerchandise
      )
        ? lineItem.numberOfWearers
        : ''
    })
  },
  {
    header: {
      display: props.intl.formatMessage({ id: 'number_of_items_per_wearer' })
    },
    accessor: (lineItem) => ({
      display: isFieldVisible(
        'numberOfItemsPerWearer',
        lineItem.billMethod,
        lineItem.numItemsPerWearer,
        lineItem.typeOfMerchandise
      )
        ? lineItem.numItemsPerWearer
        : ''
    })
  },
  ...priceColumns.map((field) => ({
    header: {
      display: props.intl.formatMessage({
        id:
          field === 'rentalPrice' ? 'rental_price' :
            field === 'salesPrice' ? 'sales_price' :
              field === 'replacePrice' ? 'replace_price' :
                field === 'washPrice' ? 'wash_price' :
                  field
      }),
      sortBy: field
    },
    accessor: (lineItem) => {
      const overrideBase = isWithinTriggerPeriod(lineItem, triggerDays);
      const base = lineItem[field].base;
      const adjusted = lineItem[field].adjusted;
      const displayAdjusted = overrideBase ? base : adjusted;
      const discount = overrideBase ? 0 : calculateDiscount(base, adjusted);

      return {
        display: isFieldVisible(field, lineItem.billMethod, base)
          ? getAdjustedCellComponent(
            formatAsMoneyString(base),
            formatAsMoneyString(Math.round(displayAdjusted*1000) / 1000),
            lineItem[field].requiresApproval,
            discount
          )
          : ''
      };
    },
    shared: {
      isNumeric: true
    }
  })),
  {
    header: {
      display: props.intl.formatMessage({ id: 'weekly_usg' }),
      sortBy: 'weeklyUsage'
    },
    accessor: (lineItem) => ({
      display: isFieldVisible('weeklyUsage', lineItem.billMethod, lineItem.weeklyUsage.base)
        ? getAdjustedCellComponent(
          lineItem.weeklyUsage.base,
          lineItem.weeklyUsage.adjusted,
          lineItem.weeklyUsage.requiresApproval
        )
        : ''
    }),
    shared: {
      isNumeric: true
    }
  },
  {
    header: {
      display: props.intl.formatMessage({ id: 'rent_qty' }),
      sortBy: 'quantity'
    },
    accessor: (lineItem) => ({
      display: isFieldVisible('quantity', lineItem.billMethod, lineItem.quantity.base)
        ? getAdjustedCellComponent(
          lineItem.quantity.base,
          lineItem.quantity.adjusted,
          lineItem.quantity.requiresApproval
        )
        : ''
    }),
    shared: {
      isNumeric: true
    }
  },
  {
    header: {
      display: props.intl.formatMessage({ id: 'del_qty' }),
      sortBy: 'deliveryQuantity'
    },
    accessor: (lineItem) => ({
      display: isFieldVisible('deliveryQuantity', lineItem.billMethod, lineItem.deliveryQuantity.base)
        ? getAdjustedCellComponent(
          lineItem.deliveryQuantity.base,
          lineItem.deliveryQuantity.adjusted,
          lineItem.deliveryQuantity.requiresApproval
        )
        : ''
    }),
    shared: {
      isNumeric: true
    }
  },
  {
    header: {
      display: props.intl.formatMessage({ id: 'circ_inv' }),
      sortBy: 'circulatingInventory'
    },
    accessor: (lineItem) => ({
      display: isFieldVisible('circulatingInventory', lineItem.billMethod, lineItem.circulatingInventory.base)
        ? getAdjustedCellComponent(
          lineItem.circulatingInventory.base,
          lineItem.circulatingInventory.adjusted,
          lineItem.circulatingInventory.requiresApproval
        )
        : ''
    }),
    shared: {
      isNumeric: true
    }
  },
  {
    header: {
      display: props.intl.formatMessage({ id: 'inventory_maintenance' }),
      sortBy: 'inventoryMaintenance'
    },
    accessor: (lineItem) => {
      const inventoryMaintenanceType = lineItem.inventoryMaintenance.inventoryMaintenanceType;
      return {
        display: isFieldVisible('inventoryMaintenance', lineItem.billMethod, inventoryMaintenanceType, lineItem.typeOfMerchandise)
          ? getAdjustedCellComponent(
            getInventoryMaintenance(lineItem.inventoryMaintenance.base, inventoryMaintenanceType),
            getInventoryMaintenance(lineItem.inventoryMaintenance.adjusted, inventoryMaintenanceType),
            lineItem.inventoryMaintenance.requiresApproval
          )
          : ''
      };
    },
    shared: {
      isNumeric: true
    }
  },
  {
    header: {
      display: props.intl.formatMessage({ id: 'image_care_price' }),
      sortBy: 'easyCarePrice'
    },
    accessor: (lineItem) => {
      const base = lineItem.easyCarePrice;
      return {
        display: isFieldVisible('easyCarePrice', lineItem.billMethod, base, lineItem.typeOfMerchandise)
          ? formatAsMoneyString(base)
          : ''
      };
    },
    shared: {
      isNumeric: true
    }
  }
]};

const sortLineItems = (lineItems, sortBy) => {
  const sorted = cloneDeep(lineItems);
  sorted.sort(sortBys[sortBy.name](sortBy.direction));
  return sorted;
};

export { columns as getColumns, sortLineItems };